import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { BehaviorSubject, Subject } from 'rxjs';

const C = React.createContext({});

export const MicroFrontContext = ({ notifier: notifierRef, playContactRequestNotification, children }) => {
    const notifier = useRef(new Subject());
    const [alerts, setAlerts] = useState([]);
    const states = useRef({});

    useEffect(() => {
        if (typeof notifierRef === 'function') {
            notifierRef(notifier.current);
        } else {
            notifierRef.current = notifier.current;
        }
    }, [notifierRef]);

    const getMicroFrontState = useCallback(
        (slug) => states.current[slug] || (states.current[slug] = new BehaviorSubject({})),
        [],
    );

    const updateMicroFrontState = useCallback(
        (slug, state) => {
            const subject = getMicroFrontState(slug);
            if (typeof state === 'function') {
                state = state(subject.value);
            }
            subject.next({ ...subject.value, ...state });
        },
        [getMicroFrontState],
    );

    const openAlert = useCallback(
        (alert, callback) =>
            setAlerts((current) => {
                const updated = [...current];
                const index = current.findIndex(({ id }) => id === alert.id);
                if (index === -1) {
                    updated.unshift(alert);
                } else {
                    try {
                        callback?.(current[index]);
                    } catch (e) {
                        console.error('Error calling open alert callback', e);
                    }
                    updated.splice(index, 1, alert);
                }
                return updated;
            }),
        [],
    );

    const closeAlert = useCallback(
        (id, callback) =>
            setAlerts((current) => {
                const index = current.findIndex(({ id: alertId }) => alertId === id);
                if (index !== -1) {
                    try {
                        callback?.(current[index]);
                    } catch (e) {
                        console.error('Error calling close alert callback', e);
                    }
                    const updated = [...current];
                    updated.splice(index, 1);
                    return updated;
                }
                return current;
            }),
        [],
    );

    return (
        <C.Provider
            value={{
                getMicroFrontState,
                updateMicroFrontState,
                alerts,
                openAlert,
                closeAlert,
                notifier: notifier.current,
                playContactRequestNotification,
            }}
        >
            {children}
        </C.Provider>
    );
};

export const useMicroFrontContext = () => useContext(C);

export default MicroFrontContext;
